import styled from "styled-components";
import React, { useEffect, useMemo, useRef, useState } from "react";

import L from "leaflet";
// get icons on https://apidocs.geoapify.com/playground/icon
import markerTaxi from "../../marker-taxi.png";
import markerHouse from "../../marker-house.png";
import "leaflet/dist/leaflet.css";

import moment from "moment-timezone";
import { getCoords } from "../geocode";

const markerTaxiIcon = L.icon({
  iconUrl: markerTaxi,
  iconSize: [25, 41],
  iconAnchor: [12.5, 41],
  popupAnchor: [0, -41],
});

const markerHouseIcon = L.icon({
  iconUrl: markerHouse,
  iconSize: [25, 41],
  iconAnchor: [12.5, 41],
  popupAnchor: [0, -41],
});

const MapContainer = styled.div`
  width: 100%;
  height: 40vh;
  position: relative;
  z-index: 0;
  margin-top: 1em;
  overflow: hidden;
`;

function TrackMap({ id, pos, pickupPos, mapResize }) {
  const [map, setMap] = useState(null);
  const userMarker = useRef(null);
  const pickupMarker = useRef(null);

  useEffect(() => {
    const _map = L.map(id);
    L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
      attribution: "© OpenStreetMap",
    }).addTo(_map);

    setMap(_map);
  }, [id]);

  useEffect(() => {
    if (!map) return;
    map._onResize();
  }, [map, mapResize]);

  useEffect(() => {
    if (!map) return;

    if (!userMarker.current && pos[0] && pos[1]) {
      userMarker.current = L.marker(pos, { icon: markerTaxiIcon });
      userMarker.current.addTo(map);
    }
    if (!pickupMarker.current && pickupPos[0] && pickupPos[1]) {
      pickupMarker.current = L.marker(pickupPos, { icon: markerHouseIcon });
      pickupMarker.current.addTo(map);
    }

    if (pos[0] && pos[1]) userMarker.current.setLatLng(pos);
    if (pickupPos[0] && pickupPos[1]) pickupMarker.current.setLatLng(pickupPos);

    map.setView(pos, 13);
  }, [map, pickupPos, pos]);

  return <MapContainer id={id}></MapContainer>;
}

function TrackSection({ stopObj, mapResize, pickupCoords }) {
  return (
    <>
      <br />
      <h4>{stopObj.title}</h4>
      <div>
        Datetime: {moment(stopObj.datetime).format("dddd DD MMM YYYY HH:mm")}
        <TrackMap
          id={stopObj.id}
          pos={stopObj.coords}
          pickupPos={pickupCoords}
          mapResize={mapResize}
        />
      </div>
    </>
  );
}

export default function FormTrack({ booking, isLoaded, mapResize }) {
  const [pickupCoords, setPickupCoords] = useState([0, 0]);

  useEffect(() => {
    if (booking.pickup?.address) {
      getCoords(
        booking.pickup.address.replaceAll(/\([^)]*\)/g, ""),
        booking.pickup?.place_id
      ).then(
        (res) => {
          setPickupCoords([res.lat, res.lon]);
        }
      );
    }
  }, [booking.pickup?.address, booking.pickup?.place_id]);

  let stopList = useMemo(() => {
    let res = booking.stopList ? booking.stopList.slice() : [];
    res = res.map((stop, i) => ({
      ...stop,
      id: `stop-${i}-map`,
      title: `Stop ${i + 1}`,
    }));

    if (booking.noShow) {
      res.push({
        ...booking.noShow,
        id: "no-show-map",
        title: "No show:",
      });
    }
    if (booking.onTheWayInfos) {
      res.push({
        ...booking.onTheWayInfos,
        id: "on-the-way-map",
        title: "En route vers le départ:",
      });
    }
    if (booking.onPlaceInfos) {
      res.push({
        ...booking.onPlaceInfos,
        id: "on-place-map",
        title: "Sur place:",
      });
    }
    if (booking.clientOnBoardInfos) {
      res.push({
        ...booking.clientOnBoardInfos,
        id: "client-on-board-map",
        title: "Client à bord:",
      });
    }
    if (booking.tripEndInfos) {
      res.push({
        ...booking.tripEndInfos,
        id: "trip-end-map",
        title: "Fin de la course:",
      });
    }

    res.sort((a, b) => (new Date(a.datetime) < new Date(b.datetime) ? -1 : 1));

    return res;
  }, [
    booking.clientOnBoardInfos,
    booking.noShow,
    booking.onPlaceInfos,
    booking.onTheWayInfos,
    booking.stopList,
    booking.tripEndInfos,
  ]);

  if (!isLoaded)
    return (
      <div className="kt-section kt-section--first">
        <p>Chargement...</p>
      </div>
    );

  return (
    <div className="kt-section kt-section--first">
      <div className="kt-section__body">
        <p>
          <strong>Commentaire de fin de trajet:</strong>{" "}
          {booking.driver_comment}
        </p>
        {stopList.map((stop) => (
          <TrackSection
            stopObj={stop}
            mapResize={mapResize}
            pickupCoords={pickupCoords}
          />
        ))}
      </div>
    </div>
  );
}
